import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { ArrowLeft, Lock, CreditCard, Check, Loader2, AlertCircle, ShieldCheck, Tag, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { APP_NAME } from "../config/branding";
import { useAuth } from "../App";
import apiClient from "../utils/apiClient";

const COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "AU", name: "Australia" },
  { code: "GB", name: "United Kingdom" },
  { code: "NZ", name: "New Zealand" },
  { code: "IE", name: "Ireland" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "NL", name: "Netherlands" },
  { code: "AT", name: "Austria" },
  { code: "BE", name: "Belgium" },
  { code: "CH", name: "Switzerland" },
  { code: "DK", name: "Denmark" },
  { code: "FI", name: "Finland" },
  { code: "IT", name: "Italy" },
  { code: "ES", name: "Spain" },
  { code: "PT", name: "Portugal" },
  { code: "SE", name: "Sweden" },
  { code: "NO", name: "Norway" },
  { code: "SG", name: "Singapore" },
  { code: "JP", name: "Japan" },
  { code: "IN", name: "India" },
  { code: "ZA", name: "South Africa" },
];

function detectCountry() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz.startsWith("America/")) return "US";
    if (tz.startsWith("Canada/") || tz.includes("Toronto") || tz.includes("Vancouver")) return "CA";
    if (tz.startsWith("Australia/")) return "AU";
    if (tz.startsWith("Europe/London")) return "GB";
    if (tz.startsWith("Pacific/Auckland")) return "NZ";
  } catch (err) {
    console.debug('Could not detect country from timezone:', err);
  }
  return "US";
}

/**
 * Inner payment form with billing fields + Stripe PaymentElement.
 */
const PaymentForm = ({ userEmail, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [touched, setTouched] = useState({});

  const [billing, setBilling] = useState({
    name: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: detectCountry(),
  });

  const update = (field, value) => setBilling((b) => ({ ...b, [field]: value }));
  const touch = (field) => setTouched((t) => ({ ...t, [field]: true }));

  const required = ["name", "line1", "city", "state", "postal_code", "country"];
  const errors = {};
  for (const f of required) {
    if (!billing[f]?.trim()) errors[f] = true;
  }
  const billingValid = Object.keys(errors).length === 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || processing) return;

    // Touch all required fields to show errors
    const allTouched = {};
    required.forEach((f) => (allTouched[f] = true));
    setTouched(allTouched);

    if (!billingValid) return;

    setProcessing(true);
    setErrorMessage(null);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/billing/success`,
        payment_method_data: {
          billing_details: {
            name: billing.name.trim(),
            email: userEmail,
            address: {
              line1: billing.line1.trim(),
              line2: billing.line2?.trim() || "",
              city: billing.city.trim(),
              state: billing.state.trim(),
              postal_code: billing.postal_code.trim(),
              country: billing.country,
            },
          },
        },
      },
    });

    if (error) {
      const msg =
        error.type === "card_error" || error.type === "validation_error"
          ? error.message
          : "An unexpected error occurred. Please try again.";
      setErrorMessage(msg);
      onError?.(msg);
    }
    setProcessing(false);
  };

  const fieldError = (field) =>
    touched[field] && errors[field] ? "border-red-300 focus-visible:ring-red-400" : "";

  return (
    <form onSubmit={handleSubmit} data-testid="payment-form">
      <div className="space-y-5">
        {/* Billing Details */}
        <div className="space-y-3" data-testid="billing-fields">
          <p className="text-sm font-medium text-foreground">Billing Details</p>

          <div className="space-y-1.5">
            <Label htmlFor="billing-email" className="text-xs">Email</Label>
            <Input
              id="billing-email"
              value={userEmail}
              disabled
              className="bg-muted/50 text-muted-foreground"
              data-testid="billing-email-input"
            />
            <p className="text-xs text-muted-foreground">Receipt will be sent to this address</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="billing-name" className="text-xs">Full Name <span className="text-red-500">*</span></Label>
            <Input
              id="billing-name"
              value={billing.name}
              onChange={(e) => update("name", e.target.value)}
              onBlur={() => touch("name")}
              placeholder="Jane Smith"
              className={fieldError("name")}
              data-testid="billing-name-input"
            />
            {touched.name && errors.name && (
              <p className="text-xs text-red-500">Full name is required</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="billing-line1" className="text-xs">Address Line 1 <span className="text-red-500">*</span></Label>
            <Input
              id="billing-line1"
              value={billing.line1}
              onChange={(e) => update("line1", e.target.value)}
              onBlur={() => touch("line1")}
              placeholder="123 Main St"
              className={fieldError("line1")}
              data-testid="billing-line1-input"
            />
            {touched.line1 && errors.line1 && (
              <p className="text-xs text-red-500">Address is required</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="billing-line2" className="text-xs">Address Line 2</Label>
            <Input
              id="billing-line2"
              value={billing.line2}
              onChange={(e) => update("line2", e.target.value)}
              placeholder="Suite 100 (optional)"
              data-testid="billing-line2-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="billing-city" className="text-xs">City <span className="text-red-500">*</span></Label>
              <Input
                id="billing-city"
                value={billing.city}
                onChange={(e) => update("city", e.target.value)}
                onBlur={() => touch("city")}
                placeholder="New York"
                className={fieldError("city")}
                data-testid="billing-city-input"
              />
              {touched.city && errors.city && (
                <p className="text-xs text-red-500">Required</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="billing-state" className="text-xs">State / Province <span className="text-red-500">*</span></Label>
              <Input
                id="billing-state"
                value={billing.state}
                onChange={(e) => update("state", e.target.value)}
                onBlur={() => touch("state")}
                placeholder="NY"
                className={fieldError("state")}
                data-testid="billing-state-input"
              />
              {touched.state && errors.state && (
                <p className="text-xs text-red-500">Required</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="billing-postal" className="text-xs">Postal / ZIP Code <span className="text-red-500">*</span></Label>
              <Input
                id="billing-postal"
                value={billing.postal_code}
                onChange={(e) => update("postal_code", e.target.value)}
                onBlur={() => touch("postal_code")}
                placeholder="10001"
                className={fieldError("postal_code")}
                data-testid="billing-postal-input"
              />
              {touched.postal_code && errors.postal_code && (
                <p className="text-xs text-red-500">Required</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="billing-country" className="text-xs">Country <span className="text-red-500">*</span></Label>
              <select
                id="billing-country"
                value={billing.country}
                onChange={(e) => update("country", e.target.value)}
                onBlur={() => touch("country")}
                className={`w-full h-10 px-3 bg-background border border-input rounded-md text-sm ${fieldError("country")}`}
                data-testid="billing-country-select"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-border" />

        {/* Payment */}
        <div className="space-y-3">
          <p className="text-sm font-medium text-foreground">Payment Method</p>
          <PaymentElement id="payment-element" options={{ layout: "tabs" }} />
        </div>

        {errorMessage && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200" data-testid="payment-error">
            <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700">{errorMessage}</p>
          </div>
        )}

        <Button
          type="submit"
          disabled={!stripe || !elements || processing}
          className="w-full h-12 text-base gap-2"
          data-testid="checkout-pay-btn"
        >
          {processing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Processing payment...
            </>
          ) : (
            <>
              <CreditCard className="w-4 h-4" />
              Subscribe Now
            </>
          )}
        </Button>

        <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
          <ShieldCheck className="w-3.5 h-3.5" />
          Payments are processed securely by Stripe. Cancel anytime.
        </div>
      </div>
    </form>
  );
};

/**
 * Main Checkout page with on-site Stripe Elements.
 */
const Checkout = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const planKey = searchParams.get("plan_key");
  const interval = searchParams.get("interval") || "annual";
  const promoParam = searchParams.get("promo") || sessionStorage.getItem("founding_promo") || "";

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clientSecret, setClientSecret] = useState(null);
  const [intentLoading, setIntentLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stripeKeyMissing, setStripeKeyMissing] = useState(false);
  const stripeRef = useRef(null);

  // Promo code state
  const [promoInput, setPromoInput] = useState(promoParam);
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoMessage, setPromoMessage] = useState(null);
  const [promoChecking, setPromoChecking] = useState(false);

  useEffect(() => {
    if (!planKey) {
      navigate("/pricing");
      return;
    }
    initCheckout();
  }, [planKey]);

  const validatePromoCode = async (code) => {
    try {
      const res = await apiClient.post("/billing/validate-promo", {
        plan_key: planKey,
        interval,
        code,
      }, { skipErrorToast: true });
      return res.data;
    } catch {
      return { valid: false, message: "Could not validate that code — standard pricing applies." };
    }
  };

  const initCheckout = async () => {
    let pk = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

    if (!pk) {
      try {
        const configRes = await apiClient.get("/billing/stripe-config", { skipErrorToast: true });
        pk = configRes.data?.publishable_key || "";
      } catch (err) {
        console.debug('Could not fetch Stripe config:', err);
      }
    }

    if (!pk) {
      setStripeKeyMissing(true);
    } else {
      stripeRef.current = loadStripe(pk);
    }

    try {
      const res = await apiClient.get("/pricing", { skipErrorToast: true });
      const plans = res.data.plans || [];
      const found = plans.find((p) => p.plan_key === planKey);

      if (!found) { setError("Plan not found."); return; }
      if (!found.is_sellable) { setError("This plan is not available for purchase."); return; }

      const checkAvail = interval === "annual" ? found.checkout_available_annual : found.checkout_available_monthly;
      if (!checkAvail) {
        setError(`${interval === "annual" ? "Annual" : "Monthly"} billing is not available for this plan.`);
        return;
      }

      setPlan(found);

      if (pk) {
        // Pre-filled promo from URL/banner: validate before creating the intent
        let promoCode = null;
        if (promoParam) {
          const v = await validatePromoCode(promoParam);
          if (v.valid) {
            setAppliedPromo(v);
            promoCode = v.code;
          } else {
            setPromoMessage(v.message);
          }
        }
        await createIntent(found, promoCode);
      }
    } catch {
      setError("Failed to load plan details.");
    } finally {
      setLoading(false);
    }
  };

  const createIntent = async (planData, promoCode = null) => {
    setIntentLoading(true);
    setError(null);
    try {
      const res = await apiClient.post("/billing/create-intent", {
        plan_key: planData?.plan_key || planKey,
        interval,
        ...(promoCode ? { promo_code: promoCode } : {}),
      });
      setClientSecret(res.data.client_secret);
      if (promoCode && !res.data.promo_applied) {
        // Server-side re-check failed (e.g. cap just reached) — fall back to full price
        setAppliedPromo(null);
        setPromoMessage(res.data.promo_message || "The founding program is full — standard pricing applies.");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to initialize checkout. Please try again.");
    } finally {
      setIntentLoading(false);
    }
  };

  const handleApplyPromo = async () => {
    const code = promoInput.trim();
    if (!code || promoChecking || intentLoading) return;
    setPromoChecking(true);
    setPromoMessage(null);
    const v = await validatePromoCode(code);
    setPromoChecking(false);
    if (v.valid) {
      setAppliedPromo(v);
      await createIntent(plan, v.code);
    } else {
      setPromoMessage(v.message);
      if (appliedPromo) {
        // Previously applied promo no longer valid — revert to full price intent
        setAppliedPromo(null);
        await createIntent(plan, null);
      }
    }
  };

  const handleRemovePromo = async () => {
    if (promoChecking || intentLoading) return;
    setAppliedPromo(null);
    setPromoMessage(null);
    setPromoInput("");
    sessionStorage.removeItem("founding_promo");
    await createIntent(plan, null);
  };

  const price = plan ? (interval === "annual" ? plan.price_annual : plan.price_monthly) : 0;
  const compareAt = plan ? (interval === "annual" ? plan.compare_at_annual : plan.compare_at_monthly) : null;
  const showCompare = plan?.show_compare_at && compareAt && compareAt > price;
  const fmt = (n) => (Number(n) % 1 === 0 ? Number(n).toLocaleString() : Number(n).toFixed(2));
  const unit = interval === "annual" ? "yr" : "mo";

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const elementsOptions = clientSecret
    ? {
        clientSecret,
        appearance: {
          theme: "stripe",
          variables: {
            colorPrimary: "#18181b",
            colorBackground: "#ffffff",
            colorText: "#18181b",
            fontFamily: "system-ui, -apple-system, sans-serif",
            borderRadius: "6px",
          },
        },
      }
    : null;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b border-border/40 sticky top-0 z-40 bg-background/95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="font-heading font-semibold text-lg tracking-tight">{APP_NAME}</Link>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Lock className="w-3.5 h-3.5" /> Secure checkout
            </div>
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-start justify-center pt-12 pb-20 px-4">
        <div className="w-full max-w-md space-y-6">
          <Button
            variant="ghost" size="sm"
            onClick={() => navigate("/pricing")}
            className="gap-1.5 text-muted-foreground hover:text-foreground"
            data-testid="checkout-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back to pricing
          </Button>

          <Card data-testid="checkout-summary">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Order Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {plan ? (
                <>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-foreground" data-testid="checkout-plan-name">{plan.display_name}</p>
                      <p className="text-xs text-muted-foreground">{plan.description}</p>
                      <p className="text-xs text-muted-foreground mt-1">Billed {interval === "annual" ? "annually" : "monthly"}</p>
                    </div>
                    <div className="text-right">
                      {(showCompare || appliedPromo) && (
                        <p className="text-sm text-muted-foreground line-through" data-testid="checkout-compare-price">
                          ${fmt(appliedPromo ? price : compareAt)}/{unit}
                        </p>
                      )}
                      <p className="text-lg font-semibold text-foreground" data-testid="checkout-price">
                        ${fmt(appliedPromo ? appliedPromo.discounted_price : price)}<span className="text-sm font-normal text-muted-foreground">/{unit}</span>
                      </p>
                      {interval === "annual" && price > 0 && (
                        <p className="text-xs text-muted-foreground">
                          ${fmt(Math.round(((appliedPromo ? appliedPromo.discounted_price : price) / 12) * 100) / 100)}/mo
                        </p>
                      )}
                    </div>
                  </div>
                  {showCompare && plan.has_active_promo && !appliedPromo && (
                    <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="checkout-discount-badge">
                      <span className="text-xs font-medium text-emerald-700">Promotional discount</span>
                      <span className="text-xs font-semibold text-emerald-700">
                        -{Math.round(((compareAt - price) / compareAt) * 100)}% (save ${compareAt - price})
                      </span>
                    </div>
                  )}

                  {/* Promo code */}
                  <div className="border-t border-border pt-3 space-y-2" data-testid="promo-code-section">
                    {appliedPromo ? (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2.5 space-y-1.5" data-testid="promo-applied-box">
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
                            <Tag className="w-3.5 h-3.5" /> {appliedPromo.code} applied
                          </span>
                          <button
                            type="button"
                            onClick={handleRemovePromo}
                            className="text-emerald-700 hover:text-emerald-900"
                            aria-label="Remove promo code"
                            data-testid="promo-remove-btn"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="flex justify-between text-xs text-emerald-700" data-testid="checkout-promo-discount-row">
                          <span>Founding discount (−{Math.round(appliedPromo.percent_off)}%)</span>
                          <span className="font-semibold">−${fmt(Math.round((price - appliedPromo.discounted_price) * 100) / 100)}/{unit}</span>
                        </div>
                        <div className="flex justify-between text-xs text-emerald-700">
                          <span>{interval === "annual" ? "First year" : `Months 1–${appliedPromo.duration_months}`}</span>
                          <span className="font-semibold">${fmt(appliedPromo.discounted_price)}/{unit}</span>
                        </div>
                        <div className="flex justify-between text-xs text-emerald-800/80 border-t border-emerald-200 pt-1.5" data-testid="checkout-month13-price">
                          <span>{interval === "annual" ? "Renews at full price from year 2" : `From month ${appliedPromo.duration_months + 1}`}</span>
                          <span className="font-medium">${fmt(appliedPromo.post_promo_price)}/{unit}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          value={promoInput}
                          onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromoMessage(null); }}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleApplyPromo(); } }}
                          placeholder="Promo code"
                          className="h-9 text-sm uppercase"
                          data-testid="promo-code-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-9 px-4"
                          onClick={handleApplyPromo}
                          disabled={!promoInput.trim() || promoChecking || intentLoading}
                          data-testid="promo-apply-btn"
                        >
                          {promoChecking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Apply"}
                        </Button>
                      </div>
                    )}
                    {promoMessage && !appliedPromo && (
                      <p className="text-xs text-muted-foreground" data-testid="promo-message">{promoMessage}</p>
                    )}
                  </div>

                  <div className="border-t border-border pt-3 mt-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Total due today</span>
                      <span className="font-semibold text-foreground" data-testid="checkout-total">
                        ${fmt(appliedPromo ? appliedPromo.discounted_price : price)}/{unit}
                      </span>
                    </div>
                    {appliedPromo && (
                      <p className="text-[11px] text-muted-foreground mt-1.5" data-testid="checkout-reversion-note">
                        {interval === "annual"
                          ? `Your first year is billed at $${fmt(appliedPromo.discounted_price)}. Renews automatically at the full price of $${fmt(appliedPromo.post_promo_price)}/yr from year 2. Cancel anytime.`
                          : `You pay $${fmt(appliedPromo.discounted_price)}/mo for months 1–${appliedPromo.duration_months}. Full price of $${fmt(appliedPromo.post_promo_price)}/mo resumes automatically from month ${appliedPromo.duration_months + 1}. Cancel anytime.`}
                      </p>
                    )}
                  </div>
                  <div className="border-t border-border pt-3 space-y-1.5">
                    {(plan.feature_list || []).slice(0, 4).map((f, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <Check className="w-3.5 h-3.5 text-foreground/60 mt-0.5 flex-shrink-0" />
                        <span className="text-xs text-muted-foreground">{f}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">{error || "Plan not available."}</p>
              )}
            </CardContent>
          </Card>

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200" data-testid="checkout-error">
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {stripeKeyMissing && !error && plan && (
            <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200" data-testid="stripe-key-missing">
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-700">
                Payments disabled: missing Stripe publishable key env var. Please configure{" "}
                <code className="bg-amber-100 px-1 rounded text-xs">REACT_APP_STRIPE_PUBLISHABLE_KEY</code>{" "}
                to enable on-site checkout.
              </p>
            </div>
          )}

          {plan && !error && !stripeKeyMissing && (
            <>
              {intentLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-sm text-muted-foreground">Initializing secure payment...</span>
                </div>
              ) : clientSecret && stripeRef.current ? (
                <Elements key={clientSecret} stripe={stripeRef.current} options={elementsOptions}>
                  <PaymentForm userEmail={user?.email || ""} onError={(msg) => setError(msg)} />
                </Elements>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Checkout;
